/**
 * The catalogue: one template per kind of structure.
 *
 * Each card is a different *picture* — a mind map, an org chart, a flowchart, a fishbone, a
 * timeline, a road, a 2×2, a board, a metro map, a layered network, a concept map, a dashboard —
 * never the same tree with other words on it. The words in each are there to show how the
 * structure is used (a station written under two lines is an interchange; a detail hangs under
 * its event), not to be a subject somebody has to delete their way out of.
 */
import { sampleChart } from '@orbit-mind/domain';
import { holding, icon, node, styled } from '../outline';
import { defineTemplate } from './define';
import { MIDNIGHT, PAPER, SKY } from './looks';
import { COSMOS, DECISION, GROWTH, METRO_SP, PLANNING, TECHNICAL, THINKING } from './palettes';
const GREEN = '#16a34a';
const RED = '#dc2626';
const SALES = {
    kind: 'table',
    header: true,
    rows: [
        ['Mês', 'Vendas', 'Meta'],
        ['Jan', '42', '40'],
        ['Fev', '48', '45'],
        ['Mar', '=B3*1,1', '50'],
        ['Abr', '61', '55'],
    ],
};
const SALES_CHART = {
    kind: 'chart',
    chart: 'column',
    size: 'medium',
    points: [
        { label: 'Jan', value: 42 },
        { label: 'Fev', value: 48 },
        { label: 'Mar', value: 52.8 },
        { label: 'Abr', value: 61 },
    ],
    series: [
        { name: 'Vendas', values: [42, 48, 52.8, 61] },
        { name: 'Meta', values: [40, 45, 50, 55] },
    ],
};
const ideas = [
    defineTemplate({
        id: 'mind-map',
        name: 'Mapa mental',
        description: 'O tema no centro e ramos para os dois lados: o formato que serve para quase tudo.',
        category: 'ideas',
        keywords: ['mapa mental', 'tema central', 'ramos', 'mind map'],
        layoutId: 'curved-line-tree/left-right',
        palette: THINKING,
        look: 'filled',
        root: node('Tema central', icon('💡', 'Ideia', 'Detalhe', 'Exemplo'), icon('❓', 'Pergunta', 'Hipótese', 'Como descobrir'), icon('📌', 'Fato', 'Fonte'), icon('✅', 'Ação', 'Quem', 'Até quando'), icon('⚠️', 'Risco', 'Como evitar')),
    }),
    defineTemplate({
        id: 'radial',
        name: 'Radial',
        description: 'Tudo à volta de um centro, com o mesmo peso: para explorar um assunto sem hierarquia.',
        category: 'ideas',
        keywords: ['brainstorm', 'tempestade de ideias', 'radial', 'explorar'],
        layoutId: 'radial',
        page: MIDNIGHT,
        palette: COSMOS,
        look: 'filled',
        shape: 'capsule',
        rootShape: 'circle',
        rootStyle: { fill: '#f472b6', size: 20 },
        root: node('Centro', icon('👥', 'Pessoas', 'Quem decide', 'Quem ajuda'), icon('🧰', 'Recursos', 'O que já temos'), icon('⏱️', 'Tempo', 'Prazo'), icon('💰', 'Custos', 'Estimativa'), icon('⚠️', 'Riscos', 'O que pode dar errado'), icon('✨', 'Ideias soltas', 'Anote sem filtrar')),
    }),
    defineTemplate({
        id: 'sketch',
        name: 'Esboço',
        description: 'Texto sobre linhas, sem caixas: para anotar rápido numa reunião ou numa aula.',
        category: 'ideas',
        keywords: ['anotações', 'notas', 'esboço', 'rascunho'],
        layoutId: 'simple-tree-curved/right',
        page: PAPER,
        palette: THINKING,
        look: 'text',
        root: node('Anotações', icon('📝', 'Primeiro ponto', 'O que foi dito', 'O que ficou em aberto'), icon('🔁', 'Segundo ponto', 'Uma dúvida'), icon('➡️', 'Próximos passos', 'Quem faz o quê')),
    }),
];
const hierarchy = [
    defineTemplate({
        id: 'org-chart',
        name: 'Organograma',
        description: 'Quem responde a quem, de cima para baixo, em caixas retas.',
        category: 'hierarchy',
        keywords: ['organograma', 'equipe', 'estrutura', 'hierarquia'],
        layoutId: 'cornered-tree/down',
        palette: PLANNING,
        look: 'filled',
        shape: 'rectangle',
        rootShape: 'rectangle',
        root: node('Direção', icon('👤', 'Área A', 'Coordenação', 'Equipe'), icon('👤', 'Área B', 'Coordenação', 'Equipe'), icon('👤', 'Área C', 'Equipe')),
    }),
    defineTemplate({
        id: 'logic-tree',
        name: 'Árvore lógica',
        description: 'Uma pergunta que se abre em hipóteses, e cada hipótese no que a comprovaria.',
        category: 'hierarchy',
        keywords: ['árvore lógica', 'hipóteses', 'problema', 'análise'],
        layoutId: 'direct-line-tree/right',
        palette: DECISION,
        look: 'outline',
        root: node('Pergunta-chave', icon('🔎', 'Hipótese 1', 'Evidência', 'Como testar'), icon('🔎', 'Hipótese 2', 'Evidência', 'Como testar'), icon('🔎', 'Hipótese 3', 'Evidência')),
    }),
    defineTemplate({
        id: 'outline',
        name: 'Tópicos',
        description: 'Um documento em tópicos e subtópicos, lido de cima para baixo como um sumário.',
        category: 'hierarchy',
        keywords: ['tópicos', 'sumário', 'lista', 'estrutura de texto'],
        layoutId: 'cornered-list/down',
        page: SKY,
        palette: PLANNING,
        look: 'outline',
        shape: 'rectangle',
        root: node('Documento', node('1. Introdução', 'Contexto', 'Objetivo'), node('2. Desenvolvimento', 'Primeiro argumento', 'Segundo argumento'), node('3. Conclusão', 'Resumo')),
    }),
    defineTemplate({
        id: 'columns',
        name: 'Colunas',
        description: 'Cada ramo vira uma coluna com a sua lista: para comparar grupos lado a lado.',
        category: 'hierarchy',
        keywords: ['colunas', 'grupos', 'categorias', 'listas'],
        layoutId: 'cornered-list/right',
        palette: THINKING,
        look: 'filled',
        shape: 'rectangle',
        root: node('Assunto', icon('🅰️', 'Grupo A', 'Item', 'Item'), icon('🅱️', 'Grupo B', 'Item', 'Item'), icon('🆎', 'Grupo C', 'Item')),
    }),
];
const process = [
    defineTemplate({
        id: 'flowchart',
        name: 'Fluxograma',
        description: 'Uma pergunta por losango e um caminho por resposta, até o fim do processo.',
        category: 'process',
        keywords: ['fluxograma', 'processo', 'decisão', 'sim ou não'],
        layoutId: 'cornered-tree/down',
        page: SKY,
        palette: DECISION,
        rootShape: 'capsule',
        shape: 'rectangle',
        rootStyle: { fill: '#1d4ed8' },
        root: node('Início', styled({ shape: 'diamond', fill: '#fde68a' }, 'Condição?', styled({ fill: GREEN }, 'Sim', node('Ação', styled({ shape: 'capsule', fill: '#1d4ed8' }, 'Fim'))), styled({ fill: RED }, 'Não', node('Ajustar', 'Tentar de novo')))),
    }),
    defineTemplate({
        id: 'fishbone',
        name: 'Espinha de peixe',
        description: 'O efeito na cabeça e as causas nas espinhas: para descobrir por que algo acontece.',
        category: 'process',
        keywords: ['espinha de peixe', 'ishikawa', 'causa e efeito', 'problema'],
        layoutId: 'fishbone/right',
        palette: TECHNICAL,
        look: 'outline',
        rootStyle: { fill: '#b91c1c' },
        root: node('Efeito (o problema)', icon('👥', 'Pessoas', 'Causa'), icon('📋', 'Método', 'Causa'), icon('⚙️', 'Máquina', 'Causa'), icon('📦', 'Material', 'Causa'), icon('📏', 'Medida', 'Causa'), icon('🌍', 'Ambiente', 'Causa')),
    }),
    defineTemplate({
        id: 'timeline',
        name: 'Linha do tempo',
        description: 'Marcos num eixo, alternando acima e abaixo, com os detalhes de cada um.',
        category: 'process',
        keywords: ['linha do tempo', 'cronologia', 'datas', 'história', 'marcos'],
        layoutId: 'timeline',
        palette: PLANNING,
        look: 'outline',
        root: node('Linha do tempo', node('Início', 'O que acontece'), node('Etapa 2', 'Detalhe', 'Detalhe'), node('Etapa 3', 'Detalhe'), node('Marco importante', 'Por que importa'), node('Fim', 'Resultado')),
    }),
    defineTemplate({
        id: 'road',
        name: 'Caminho',
        description: 'Etapas numeradas numa estrada que faz curvas: um roteiro do começo ao fim.',
        category: 'process',
        keywords: ['caminho', 'roteiro', 'etapas', 'jornada', 'roadmap'],
        layoutId: 'road',
        palette: GROWTH,
        look: 'outline',
        root: node('Partida', icon('🔍', 'Etapa 1', 'O que entregar'), icon('🧪', 'Etapa 2'), icon('🛠️', 'Etapa 3'), icon('🧭', 'Etapa 4', 'O que entregar'), icon('🚀', 'Etapa 5'), icon('🏁', 'Chegada')),
    }),
];
const compare = [
    defineTemplate({
        id: 'matrix',
        name: 'Matriz 2×2',
        description: 'Dois critérios, quatro quadrantes: para decidir o que fazer primeiro.',
        category: 'compare',
        keywords: ['matriz', '2x2', 'quadrantes', 'prioridade', 'impacto e esforço', 'eisenhower'],
        layoutId: 'matrix',
        palette: ['#16a34a', '#2563eb', '#d97706', '#6b7280'],
        look: 'outline',
        root: node('Impacto × esforço', node('Fazer já', 'Muito impacto, pouco esforço'), node('Planejar', 'Muito impacto, muito esforço'), node('Se sobrar tempo', 'Pouco impacto, pouco esforço'), node('Evitar', 'Pouco impacto, muito esforço')),
    }),
    defineTemplate({
        id: 'board',
        name: 'Quadro de colunas',
        description: 'Colunas de cartões, como um kanban: arraste cada cartão conforme ele avança.',
        category: 'compare',
        keywords: ['quadro', 'kanban', 'tarefas', 'colunas', 'status'],
        layoutId: 'board',
        palette: PLANNING,
        look: 'outline',
        root: node('Quadro', node('A fazer', 'Tarefa', 'Tarefa'), node('Fazendo', 'Tarefa'), node('Em revisão', 'Tarefa'), node('Feito', 'Tarefa', 'Tarefa')),
    }),
    defineTemplate({
        id: 'pros-and-cons',
        name: 'Prós e contras',
        description: 'A decisão no meio, o que pesa a favor de um lado e o que pesa contra do outro.',
        category: 'compare',
        keywords: ['prós e contras', 'decisão', 'vantagens', 'desvantagens'],
        layoutId: 'direct-line-tree/left-right',
        palette: [GREEN, RED],
        look: 'solid',
        root: node('A decisão', icon('👍', 'Prós', 'Vantagem', 'Vantagem'), icon('👎', 'Contras', 'Desvantagem', 'Desvantagem')),
    }),
];
const networks = [
    defineTemplate({
        id: 'metro',
        name: 'Mapa de metrô',
        description: 'Linhas coloridas com estações: repita o nome de uma estação em duas linhas e ela vira baldeação.',
        category: 'networks',
        keywords: ['metrô', 'metro', 'linhas', 'estações', 'transporte', 'baldeação', 'rede'],
        layoutId: 'metro',
        palette: METRO_SP,
        look: 'outline',
        rootStyle: { fill: '#1f2937' },
        root: node('Mapa de metrô', node('Linha 1 · Azul', 'Jardim', 'Parque', 'Centro', 'Catedral', 'Mercado', 'Universidade'), node('Linha 2 · Verde', 'Aeroporto', 'Estádio', 'Centro', 'Museu', 'Hospital'), node('Linha 3 · Vermelha', 'Porto', 'Mercado', 'Museu', 'Rodoviária')),
    }),
    defineTemplate({
        id: 'layers',
        name: 'Rede em camadas',
        description: 'Colunas ligadas de uma camada à seguinte: entradas, o que as transforma, saídas.',
        category: 'networks',
        keywords: ['camadas', 'rede', 'fluxo', 'entradas e saídas', 'funil'],
        layoutId: 'layers',
        palette: TECHNICAL,
        look: 'outline',
        shape: 'capsule',
        root: node('Rede em camadas', node('Entradas', 'Entrada A', 'Entrada B', 'Entrada C'), node('Processo', 'Etapa X', 'Etapa Y'), node('Saídas', 'Resultado 1', 'Resultado 2', 'Resultado 3')),
    }),
    defineTemplate({
        id: 'concept-map',
        name: 'Rede de conceitos',
        description: 'Conceitos ligados por verbos nas setas: para mostrar como as ideias se relacionam.',
        category: 'networks',
        keywords: ['mapa conceitual', 'conceitos', 'relações', 'rede'],
        layoutId: 'radial',
        palette: THINKING,
        look: 'filled',
        shape: 'capsule',
        root: node('Conceito central', icon('🔵', 'Conceito A', 'Exemplo de A'), icon('🟢', 'Conceito B', 'Exemplo de B'), icon('🟠', 'Conceito C'), icon('🟣', 'Conceito D')),
        links: [
            { from: 'Conceito A', to: 'Conceito B', label: 'causa' },
            { from: 'Conceito B', to: 'Conceito C', label: 'exige' },
            { from: 'Conceito D', to: 'Conceito A', label: 'contradiz' },
        ],
    }),
];
const data = [
    defineTemplate({
        id: 'chart-dashboard',
        name: 'Painel de gráficos',
        description: 'Indicadores em colunas: um número, uma tendência, uma divisão e uma meta.',
        category: 'data',
        keywords: ['painel', 'dashboard', 'gráficos', 'indicadores', 'kpi'],
        layoutId: 'board',
        palette: PLANNING,
        look: 'outline',
        root: node('Painel', node('Número', holding('Receita do mês', sampleChart('kpi'), { width: 226, height: 154 })), node('Tendência', holding('Visitas por semana', sampleChart('line'), { width: 226, height: 178 })), node('Divisão', holding('De onde vêm os clientes', sampleChart('donut'), { width: 228, height: 166 })), node('Meta', holding('Meta do trimestre', sampleChart('gauge'), { width: 226, height: 174 }))),
    }),
    defineTemplate({
        id: 'table-and-chart',
        name: 'Tabela e gráfico',
        description: 'Uma tabela com fórmula e um gráfico que a lê: mude um número e o gráfico acompanha.',
        category: 'data',
        keywords: ['tabela', 'gráfico', 'dados', 'fórmula', 'planilha'],
        layoutId: 'direct-line-tree/right',
        palette: PLANNING,
        look: 'outline',
        root: node('Dados', {
            ...holding('Vendas por mês', SALES, { width: 168, height: 164 }),
            children: [
                holding('Vendas e meta', (idOf) => ({
                    ...SALES_CHART,
                    source: { nodeId: idOf('Vendas por mês'), labelColumn: 0, valueColumns: [1, 2] },
                }), { width: 226, height: 178 }),
            ],
        }, icon('🗒️', 'Como ler', 'Mar é uma fórmula: Fev × 1,1')),
    }),
];
export const structureTemplates = [
    ...ideas,
    ...hierarchy,
    ...process,
    ...compare,
    ...networks,
    ...data,
];
//# sourceMappingURL=structures.js.map